// src/Dashboard.js
import React, { useState, useEffect } from 'react';
import api, { API_BASE } from './apiClient';
import ApprovalMatrix from './ApprovalMatrix';
import LowPriorityIcon from '@mui/icons-material/LowPriority';
import MediumPriorityIcon from '@mui/icons-material/PriorityHigh';
import HighPriorityIcon from '@mui/icons-material/Whatshot';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import Fade from '@mui/material/Fade';
import { useSnackbar } from 'notistack';
import CalendarView from './CalendarView';
import CircularProgress from '@mui/material/CircularProgress'; // ADD THIS
import {
  AppBar,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  FormControl,
  InputLabel,
  InputAdornment,
  Checkbox,
  Menu,
  FormControlLabel,
  Tabs,
  Tab,
  Chip,
  ListItemIcon,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EmailIcon from '@mui/icons-material/Email';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import RoleManager from './RoleManager';
import DeleteIcon from '@mui/icons-material/Delete';
import ProjectRoleManager from './ProjectRoleManager';
import CustomRoleDialog from './CustomRoleDialog';
import WorkspaceInviteDialog from './WorkspaceInviteDialog';
import PermissionGrid from './PermissionGrid';
import ArchiveModal from './ArchiveModal';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ProjectMultiTagSelector from './ProjectMultiTagSelector';
import Profile from './Profile';
import TaskAssignment from './TaskAssignment';
import ProjectDialog from './ProjectDialog';
import BoardView from './BoardView';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';

import {
  descendingComparator,
  getComparator,
  stableSort,
  PRIORITIES,
  HOME_TABLE_AVAILABLE_COLUMNS,
  getDueHealth,
  getTaskTags,
  TaskTable,
  SimpleListCard,
  RecurringTasksTable,
  ProjectTasksTable,
} from './DashboardTables';
import TaskDetailPanel from './TaskDetailPanel';
import DashboardView from './DashboardView';

/* ---------- main Dashboard ---------- */
function Dashboard({ userId, onLogout, onToggleMode }) {
  // Group ALL useState hooks at the top (React requires this order to be consistent)
  const [tasks, setTasks] = useState([]);
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workspaceUsers, setWorkspaceUsers] = useState([]);
  const [projectUsers, setProjectUsers] = useState([]);
  const [goals, setGoals] = useState([]); // placeholder list
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [sidebarAutoCollapse, setSidebarAutoCollapse] = useState(false);
  const [sidebarHoverExpand, setSidebarHoverExpand] = useState(true);
  const [sidebarHovering, setSidebarHovering] = useState(false);
  const [greeting, setGreeting] = useState('');
  const [currentView, setCurrentView] = useState('home');
  const [showApprovalMatrix, setShowApprovalMatrix] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: '',
    priority: 'medium',
    dueDate: '',
    targetDate: '',
    assignedTo: '',
    status: 'to-do',
    description: '',
    file: null,
    collaborators: [],
    tags: '', // comma-separated text input
    projectId: '',
  });

  // 👇 NEW: keep files only in frontend until user clicks "Create"
  const [taskFormFiles, setTaskFormFiles] = useState([]); // Array<File>
  const [roleManagerOpen, setRoleManagerOpen] = useState(false);
  const [projectRoleManagerOpen, setProjectRoleManagerOpen] = useState(false);
  const [customRoleDialogOpen, setCustomRoleDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [permissionGridOpen, setPermissionGridOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const [projectNotesDraft, setProjectNotesDraft] = useState('');
  const [projectSaving, setProjectSaving] = useState(false);
  const [workspaceTab, setWorkspaceTab] = useState(0);
  const [viewMode, setViewMode] = useState('list');
  const [customRoles, setCustomRoles] = useState([]);
  const [workspaceLogs, setWorkspaceLogs] = useState([]);
  const [showArchivedTasks, setShowArchivedTasks] = useState(false);
  const [taskPanelOpen, setTaskPanelOpen] = useState(false);
  const [selectedTaskForPanel, setSelectedTaskForPanel] = useState(null);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({
    status: '',
    priority: '',
    assignee: '', // 🔁 use "assignee" to match the UI
    tags: '',
  });

  const [taskDetailSaving, setTaskDetailSaving] = useState(false);
  const [selectedTaskComments, setSelectedTaskComments] = useState([]);
  const [taskCommentDraft, setTaskCommentDraft] = useState('');
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [taskSteps, setTaskSteps] = useState([]);
  const [availableStepGroups, setAvailableStepGroups] = useState([]);
  const [selectedStepGroupForAttach, setSelectedStepGroupForAttach] =
    useState('');
  const [taskWorkLogs, setTaskWorkLogs] = useState([]);
  const [taskActivityLogs, setTaskActivityLogs] = useState([]);
  const [taskDetailTab, setTaskDetailTab] = useState('comments'); // 'comments' | 'work' | 'activity'
  const [activityFrom, setActivityFrom] = useState('');
  const [activityTo, setActivityTo] = useState('');
  const [activityUserFilter, setActivityUserFilter] = useState('');
  const [taskWorkLogDraft, setTaskWorkLogDraft] = useState(() => ({
    date: new Date().toISOString().slice(0, 10),
    hours: '',
    minutes: '',
    notes: '',
    approver_user_id: '',
  }));
  const [availableWorkLogApprovers, setAvailableWorkLogApprovers] = useState(
    []
  );
  const [taskStatusFilter, setTaskStatusFilter] = useState('all');
  const [taskPriorityFilter, setTaskPriorityFilter] = useState('all');
  const [taskAssigneeFilter, setTaskAssigneeFilter] = useState('all');
  const [workDoneRangeKey, setWorkDoneRangeKey] = useState('this_month'); // today, yesterday, last7, this_month, last_month, custom
  const [workDoneCustomFrom, setWorkDoneCustomFrom] = useState('');
  const [workDoneCustomTo, setWorkDoneCustomTo] = useState('');
  const [workDoneRows, setWorkDoneRows] = useState([]);
  const [workDoneSummary, setWorkDoneSummary] = useState([]);
  const [workDoneSelectedDate, setWorkDoneSelectedDate] = useState('');
  const [workDoneViewMode, setWorkDoneViewMode] = useState('mine'); // mine | team
  const [workDoneStatusFilter, setWorkDoneStatusFilter] = useState('all'); // all | pending | approved
  const [workDoneProjectFilterIds, setWorkDoneProjectFilterIds] = useState([]);
  const [taskTagFilter, setTaskTagFilter] = useState('all');
  const [taskDuePreset, setTaskDuePreset] = useState('all');
  const [taskTargetPreset, setTaskTargetPreset] = useState('all');
  const [taskCreatorFilter, setTaskCreatorFilter] = useState('all');
  const [taskDueCustomFrom, setTaskDueCustomFrom] = useState('');
  const [taskDueCustomTo, setTaskDueCustomTo] = useState('');
  const [taskTargetCustomFrom, setTaskTargetCustomFrom] = useState('');
  const [taskTargetCustomTo, setTaskTargetCustomTo] = useState('');
  const [taskQuickSearch, setTaskQuickSearch] = useState('');
  const [topBarUserName, setTopBarUserName] = useState('');
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [linkedProjectIds, setLinkedProjectIds] = useState([]);
  const [projectRecurring, setProjectRecurring] = useState([]);
  const [recurringLoading, setRecurringLoading] = useState(false);
  const [adminRecurring, setAdminRecurring] = useState([]);
  const [adminRecurringLoading, setAdminRecurringLoading] = useState(false);
  const currentUser = {};
  const [adminCronRunning, setAdminCronRunning] = useState(false);
  const [homeTableColumns, setHomeTableColumns] = useState({
    createdByMe: ['title', 'priority', 'due_date'],
    assignedToMe: ['title', 'priority', 'due_date'],
    overdue: ['title', 'priority', 'due_date'],
    dueToday: ['title', 'priority', 'due_date'],
  });
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false); // ADD THIS
  const [loadingProjects, setLoadingProjects] = useState(false); // ADD THIS
  const [loadingTasks, setLoadingTasks] = useState(false); // ADD THIS
  const [loadingWorkspaceUsers, setLoadingWorkspaceUsers] = useState(false); // ADD THIS
  const [loadingProjectUsers, setLoadingProjectUsers] = useState(false); // ADD THIS
  const { enqueueSnackbar } = useSnackbar(); // ADD THIS LINE HERE

  // Group ALL useEffect hooks next (after states)
  // Dynamic page title — 100% safe (no initialization error)
  // Perfect hierarchical page title
  useEffect(() => {
    let title = 'Krama';

    // Workspace name
    const workspace = workspaces.find(
      (w) => String(w.id) === String(selectedWorkspace)
    );
    const workspaceName = workspace?.name || 'Personal';

    // Project name
    const project = projects.find(
      (p) => String(p.id) === String(selectedProject)
    );
    const projectName = project?.name;

    // Build title step by step
    if (currentView === 'home') {
      title = 'Krama • Home';
    } else if (currentView === 'workdone') {
      title = 'Krama • Work Done';
    } else if (currentView === 'personal-tasks') {
      title = 'Krama • Personal Tasks';
    } else {
      title = `Krama • ${workspaceName}`;
      if (projectName) {
        title += ` • ${projectName}`;
        if (currentView === 'project-tasks') {
          title += ' • Tasks';
        }
      }
    }

    document.title = title;
  }, [currentView, selectedWorkspace, selectedProject, workspaces, projects]);
  // 'home' | 'workspace-overview' | 'project-overview' | 'project-tasks'

  useEffect(() => {
    if (permissionGridOpen) {
      api.get('/api/custom-roles').then((res) => setCustomRoles(res.data));
    }
  }, [permissionGridOpen]);

  useEffect(() => {
    if (!userId) return;
    setGreeting(getGreeting());
    fetchWorkspaces();
  }, [userId]);

  // 🔹 NEW: preload real user name for the top bar (even on Home)
  useEffect(() => {
    if (!userId) {
      setTopBarUserName('');
      return;
    }
    // If we already have a name, don't fetch again
    if (topBarUserName) return;
    // Wait for workspaces to load
    if (!workspaces || workspaces.length === 0) return;
    // Prefer "Personal" workspace if it exists, else first workspace
    const wsForName =
      workspaces.find((w) => w.name === 'Personal') || workspaces[0];
    const loadName = async () => {
      try {
        const res = await api.get(
          `/api/workspace-users?workspace_id=${wsForName.id}`
        );
        const users = res.data || [];
        const me = users.find((u) => Number(u.id) === Number(userId));
        if (me) {
          setTopBarUserName(me.username || me.name || `User ${userId}`);
        } else {
          setTopBarUserName(`User ${userId}`);
        }
      } catch (err) {
        console.error('Error preloading user name for top bar:', err);
        setTopBarUserName(`User ${userId}`);
      }
    };
    loadName();
  }, [userId, workspaces, topBarUserName]);

  /* ---------- invitations load ---------- */
  useEffect(() => {
    const loadInvites = async () => {
      if (!userId) {
        setPendingInvites([]);
        return;
      }
      try {
        const res = await api.get(
          `/api/my-workspace-invitations?user_id=${userId}`
        );
        setPendingInvites(res.data || []);
      } catch (err) {
        console.error('Error fetching invitations:', err);
        setPendingInvites([]);
      }
    };
    loadInvites();
  }, [userId]);

  /* ---------- workspace change ---------- */
  useEffect(() => {
    if (selectedWorkspace) {
      fetchProjects(selectedWorkspace);
      fetchGoals(selectedWorkspace);
    } else {
      setProjects([]);
      setGoals([]);
    }
  }, [selectedWorkspace]);

  /* ---------- tasks on project/user change ---------- */
  useEffect(() => {
    if (userId) {
      fetchTasks();
    }
  }, [userId, selectedProject]);

  /* ---------- workspace users ---------- */
  useEffect(() => {
    const loadWorkspaceUsers = async () => {
      if (!selectedWorkspace) {
        setWorkspaceUsers([]);
        return;
      }
      setLoadingWorkspaceUsers(true); // ADD THIS
      try {
        const res = await api.get(
          `/api/workspace-users?workspace_id=${selectedWorkspace}`
        );
        setWorkspaceUsers(res.data || []);
      } catch (err) {
        console.error('Error fetching workspace users:', err);
        enqueueSnackbar(
          err.response?.data?.error || 'Failed to load workspace users.'
        );
        setWorkspaceUsers([]);
      } finally {
        setLoadingWorkspaceUsers(false); // ADD THIS
      }
    };
    loadWorkspaceUsers();
  }, [selectedWorkspace]);

  /* ---------- project users ---------- */
  useEffect(() => {
    const loadProjectUsers = async () => {
      if (!selectedProject) {
        setProjectUsers([]);
        return;
      }
      setLoadingProjectUsers(true); // ADD THIS
      try {
        const res = await api.get(
          `/api/project-users?project_id=${selectedProject}`
        );
        setProjectUsers(res.data || []);
      } catch (err) {
        console.error('Error fetching project users:', err);
        enqueueSnackbar(
          err.response?.data?.error || 'Failed to load project users.'
        );
        setProjectUsers([]);
      } finally {
        setLoadingProjectUsers(false); // ADD THIS
      }
    };
    loadProjectUsers();
  }, [selectedProject]);

  useEffect(() => {
    setTaskStatusFilter('all');
    setTaskPriorityFilter('all');
    setTaskAssigneeFilter('all');
    setTaskTagFilter('all');
  }, [selectedProject]);

  useEffect(() => {
    if (currentView !== 'workdone' || !userId) return;
    const { start, end } = computeDateRange(
      workDoneRangeKey,
      workDoneCustomFrom,
      workDoneCustomTo
    );
    if (workDoneRangeKey === 'custom' && (!start || !end)) {
      // wait for both dates
      return;
    }
    const load = async () => {
      try {
        const params = new URLSearchParams();
        params.append('user_id', userId);
        params.append('view', workDoneViewMode);
        if (selectedWorkspace) {
          params.append('workspace_id', selectedWorkspace);
        }
        if (start) params.append('date_from', start);
        if (end) params.append('date_to', end);
        if (workDoneStatusFilter && workDoneStatusFilter !== 'all') {
          params.append('status', workDoneStatusFilter);
        }
        if (workDoneProjectFilterIds.length > 0) {
          params.append('project_ids', workDoneProjectFilterIds.join(','));
        }
        const res = await api.get(`/api/work-logs?${params.toString()}`);
        setWorkDoneRows(res.data || []);
        const sumRes = await api.get(
          `/api/work-logs/summary?${params.toString()}`
        );
        setWorkDoneSummary(sumRes.data || []);
      } catch (err) {
        console.error('Error loading work done view:', err);
        setWorkDoneRows([]);
        setWorkDoneSummary([]);
      }
    };
    load();
  }, [
    currentView,
    userId,
    workDoneRangeKey,
    workDoneCustomFrom,
    workDoneCustomTo,
    workDoneViewMode,
    workDoneStatusFilter,
    workDoneProjectFilterIds,
    selectedWorkspace,
  ]);

  // Load project-wise recurring rules when on Project Recurring view
  useEffect(() => {
    if (currentView !== 'project-recurring') return;
    if (!selectedWorkspace || !selectedProject) {
      setProjectRecurring([]);
      return;
    }
    loadProjectRecurring();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, selectedWorkspace, selectedProject]);

  // Load workspace-wide recurring rules for admin view
  useEffect(() => {
    if (currentView !== 'recurring-admin') return;
    if (!selectedWorkspace) {
      setAdminRecurring([]);
      return;
    }
    loadAdminRecurring();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, selectedWorkspace]);

  // Derived constants (move these AFTER all hooks)
  const selectedWorkspaceObj = workspaces.find(
    (w) => String(w.id) === String(selectedWorkspace)
  );
  const selectedProjectObj = projects.find(
    (p) => String(p.id) === String(selectedProject)
  );
  const isPersonalWorkspace =
    selectedWorkspaceObj && selectedWorkspaceObj.name === 'Personal';
  const assignableUsers =
    selectedProject && !isPersonalWorkspace ? projectUsers : workspaceUsers;
  const canLogWorkOnSelectedTask = selectedTaskForPanel
    ? Number(selectedTaskForPanel.user_id) === Number(userId) ||
      (selectedTaskForPanel.assigned_to &&
        Number(selectedTaskForPanel.assigned_to) === Number(userId)) ||
      (Array.isArray(selectedTaskForPanel.collaborators) &&
        selectedTaskForPanel.collaborators.some(
          (c) => Number(c.user_id) === Number(userId)
        ))
    : false;

  // Functions (keep these after derived constants)
  const computeDateRange = (rangeKey, customFrom, customTo) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    let start = '';
    let end = '';
    switch (rangeKey) {
      case 'today': {
        start = fmt(today);
        end = fmt(today);
        break;
      }
      case 'yesterday': {
        const y = new Date(today);
        y.setDate(y.getDate() - 1);
        start = fmt(y);
        end = fmt(y);
        break;
      }
      case 'last7': {
        const s = new Date(today);
        s.setDate(s.getDate() - 6);
        start = fmt(s);
        end = fmt(today);
        break;
      }
      case 'last_month': {
        const firstThis = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastPrev = new Date(firstThis);
        lastPrev.setDate(0); // last day prev month
        const firstPrev = new Date(
          lastPrev.getFullYear(),
          lastPrev.getMonth(),
          1
        );
        start = fmt(firstPrev);
        end = fmt(lastPrev);
        break;
      }
      case 'this_month': {
        const first = new Date(today.getFullYear(), today.getMonth(), 1);
        start = fmt(first);
        end = fmt(today);
        break;
      }
      case 'custom':
      default: {
        start = customFrom || '';
        end = customTo || '';
        break;
      }
    }
    return { start, end };
  };

  /* ---------- API helpers ---------- */
  const loadProjectRecurring = async () => {
    if (!selectedWorkspace || !selectedProject) {
      setProjectRecurring([]);
      return;
    }
    try {
      setRecurringLoading(true);
      const res = await api.get('/api/recurring-tasks', {
        params: {
          workspace_id: selectedWorkspace,
          project_id: selectedProject,
        },
      });
      setProjectRecurring(res.data || []);
    } catch (err) {
      console.error('Error loading project recurring tasks:', err);
      setProjectRecurring([]);
    } finally {
      setRecurringLoading(false);
    }
  };

  const loadAdminRecurring = async () => {
    if (!selectedWorkspace) {
      setAdminRecurring([]);
      return;
    }
    try {
      setAdminRecurringLoading(true);
      const res = await api.get('/api/recurring-tasks', {
        params: {
          workspace_id: selectedWorkspace,
          view: 'workspace',
        },
      });
      setAdminRecurring(res.data || []);
    } catch (err) {
      console.error('Error loading workspace recurring tasks:', err);
      setAdminRecurring([]);
    } finally {
      setAdminRecurringLoading(false);
    }
  };

  const handleRecurringAction = async (recurringId, action) => {
    try {
      await api.post(`/api/recurring-tasks/${recurringId}/actions`, {
        action,
        user_id: userId,
      });
      if (currentView === 'project-recurring') {
        loadProjectRecurring();
      } else if (currentView === 'recurring-admin') {
        loadAdminRecurring();
      }
    } catch (err) {
      console.error('Error performing recurring action:', err);
      enqueueSnackbar(
        err.response?.data?.error ||
          `Error performing action "${action}" on recurring rule.`
      );
    }
  };

  const handleRunRecurringNow = async () => {
    if (!selectedWorkspace) return;
    try {
      setAdminCronRunning(true);
      await api.post('/api/recurring-tasks/run', {
        workspace_id: selectedWorkspace,
        user_id: userId,
      });
      // Reload after manual run
      loadAdminRecurring();
    } catch (err) {
      console.error('Error running recurring-tasks cron:', err);
      enqueueSnackbar(
        err.response?.data?.error ||
          'Error running recurring scheduler. Check server logs.'
      );
    } finally {
      setAdminCronRunning(false);
    }
  };

  const fetchWorkspaces = async () => {
    setLoadingWorkspaces(true); // ADD THIS
    try {
      const res = await api.get(`/api/workplaces?user_id=${userId}`);
      const rows = res.data || [];
      setWorkspaces(rows);
    } catch (err) {
      console.error('Error fetching workplaces:', err);
      enqueueSnackbar(
        err.response?.data?.error || 'Failed to load workspaces.'
      ); // ADD USER ALERT
      setWorkspaces([]); // Fallback
    } finally {
      setLoadingWorkspaces(false); // ADD THIS
    }
  };

  const fetchProjects = async (workspaceId) => {
    if (!workspaceId) {
      setProjects([]);
      return;
    }
    setLoadingProjects(true); // ADD THIS
    try {
      const res = await api.get(
        `/api/projects?user_id=${userId}&workplace_id=${workspaceId}`
      );
      setProjects(res.data || []);
    } catch (err) {
      console.error('Error fetching projects:', err);
      enqueueSnackbar(
        err.response?.data?.error || 'Failed to load projects.'
      );
      setProjects([]);
    } finally {
      setLoadingProjects(false); // ADD THIS
    }
  };

  const fetchGoals = async (workspaceId) => {
    try {
      // safe even if not implemented yet
      const res = await api.get(`/api/goals?workspace_id=${workspaceId}`);
      setGoals(res.data || []);
    } catch (err) {
      console.warn('Goals endpoint not ready yet:', String(err));
      setGoals([]);
    }
  };

  const fetchTasks = async () => {
    setLoadingTasks(true);
    try {
      let url = `/api/tasks?user_id=${userId}`;
      if (selectedProject) {
        url += `&project_id=${selectedProject}`;
      }
      const res = await api.get(url);
      const allFetchedTasks = res.data || [];

      // Always store ALL fetched tasks in 'tasks'
      setTasks(allFetchedTasks);

      // Assigned tasks = those assigned to current user (from the fetched list)
      setAssignedTasks(
        allFetchedTasks.filter(
          (t) => Number(t.assigned_to) === Number(userId)
        )
      );
    } catch (err) {
      console.error('Error fetching tasks:', err);
      enqueueSnackbar(
        err.response?.data?.error || 'Failed to load tasks.',
        { variant: 'error' }
      );
      setTasks([]);
      setAssignedTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  /* ---------- helpers ---------- */
  const getGreeting = () => {
    const now = new Date();
    const hour = now.getHours();
    let timeGreeting = 'Good morning';
    if (hour >= 12 && hour < 18) timeGreeting = 'Good afternoon';
    if (hour >= 18) timeGreeting = 'Good evening';
    const today = now.toDateString();
    let loginCount = localStorage.getItem('loginCount') || 0;
    const lastLogin = localStorage.getItem('lastLogin');
    if (lastLogin !== today) {
      loginCount = 1;
      localStorage.setItem('lastLogin', today);
    } else {
      loginCount = parseInt(loginCount, 10) + 1;
    }
    localStorage.setItem('loginCount', loginCount);
    const welcome = loginCount === 1 ? 'Welcome' : 'Welcome back';
    return `${welcome} [userId], ${timeGreeting} [userId]`;
  };

  const handleOpenProfileMenu = (event) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleCloseProfileMenu = () => {
    setProfileMenuAnchor(null);
  };

  const handleWorkspaceChange = (event) => {
    const wsId = event.target.value;
    setSelectedWorkspace(wsId);
    setSelectedProject('');
    const wsObj = workspaces.find((w) => String(w.id) === String(wsId));
    if (wsId && wsObj) {
      if (wsObj.name === 'Personal') {
        // Personal workspace → go directly to personal tasks page
        setCurrentView('personal-tasks');
      } else {
        // Non-Personal workspace → Workspace overview
        setCurrentView('workspace-overview');
      }
    } else {
      // Cleared selection → back to Home
      setCurrentView('home');
    }
  };

  const handleModify = async (taskId, field, value) => {
    if (!userId) return;

    const payload = { user_id: userId };

    switch (field) {
      case 'title':
        payload.title = value;
        break;

      case 'priority':
        payload.priority = value;
        break;

      case 'status':
        payload.status = value;
        break;

      case 'due_date':
        payload.due_date = value || null;
        break;

      case 'target_date':
        payload.target_date = value || null;
        break;

      case 'assigned_to':
        payload.assigned_to = value || null;
        break;

      default:
        return;
    }

    try {
      const res = await api.put(`/api/tasks/${taskId}`, payload);
      const updated = res.data;

      setTasks((prev) =>
        (prev || []).map((t) => (t.id === taskId ? updated : t))
      );
      setAssignedTasks((prev) =>
        (prev || []).map((t) => (t.id === taskId ? updated : t))
      );
    } catch (err) {
      console.error('Error updating task from Dashboard:', err);
      const msg =
        err.response?.data?.error || 'Error updating task. Check console.';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleCreateProject = async () => {
    if (!projectName.trim()) {
      enqueueSnackbar('Enter project name');
      return;
    }
    if (!selectedWorkspace) {
      enqueueSnackbar('Select a workspace first');
      return;
    }
    try {
      const res = await api.post('/api/projects', {
        name: projectName,
        owner_id: userId,
        workplace_id: selectedWorkspace,
      });
      const created = res.data;
      setProjects((prev) => [created, ...prev]);
      setProjectDialogOpen(false);
      setProjectName('');
      setSelectedProject(String(created.id));
    } catch (err) {
      console.error('Error creating project:', err);
      enqueueSnackbar('Error creating project');
    }
  };

  const handleSaveProjectNotes = async () => {
    try {
      if (!selectedProject) {
        enqueueSnackbar('No project selected', { variant: 'warning' });
        return;
      }

      const payload = {
        name: selectedProjectObj?.name || '',
        notes: projectNotesDraft.trim(),
        user_id: userId,
      };

      setProjectSaving(true);

      await api.put(`/api/projects/${selectedProject}`, payload);

      enqueueSnackbar('Project notes updated', { variant: 'success' });
    } catch (err) {
      const backendData = err?.response?.data;
      console.error('Error saving project notes', err);
      enqueueSnackbar(
        backendData?.error || backendData?.message || 'Failed to edit project',
        { variant: 'error' }
      );
    } finally {
      setProjectSaving(false);
    }
  };

  // Keep notes draft in sync with the selected project
  useEffect(() => {
    if (selectedProjectObj) {
      setProjectNotesDraft(selectedProjectObj.notes || '');
    } else {
      setProjectNotesDraft('');
    }
  }, [selectedProjectObj?.id, selectedProjectObj?.notes]);

  const handleCreateTask = async (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    if (!taskDialogOpen) return;

    try {
      if (!taskForm.title || !taskForm.title.trim()) {
        enqueueSnackbar('Title is required', { variant: 'warning' });
        return;
      }

      const normalizedTags = (taskForm.tags || '')
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean)
        .join(',');

      const formData = new FormData();
      formData.append('user_id', String(userId));
      formData.append('title', taskForm.title.trim());
      formData.append('priority', taskForm.priority || 'medium');
      formData.append('status', taskForm.status || 'to-do');
      formData.append('description', taskForm.description || '');

      if (normalizedTags) {
        formData.append('tags', normalizedTags);
      }

      if (taskForm.projectId != null && taskForm.projectId !== '') {
        formData.append('project_id', String(taskForm.projectId));
      }
      if (taskForm.targetDate) {
        formData.append('target_date', taskForm.targetDate);
      }
      if (taskForm.dueDate) {
        formData.append('due_date', taskForm.dueDate);
      }

      if (taskForm.assignedTo != null && taskForm.assignedTo !== '') {
        formData.append('assigned_to', String(taskForm.assignedTo));
      }

      const collabIds = (taskForm.collaborators || []).map((id) =>
        Number(id)
      );
      if (collabIds.length > 0) {
        formData.append('collaborators', JSON.stringify(collabIds));
      }

      if (taskForm.file) {
        formData.append('file', taskForm.file);
      }

      const res = await api.post('/api/tasks', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const created = res.data;

      if (normalizedTags) {
        try {
          await api.put(`/api/tasks/${created.id}`, {
            user_id: userId,
            tags: normalizedTags,
          });
        } catch (err2) {
          console.error('Error applying tags after create:', err2);
        }
      }

      enqueueSnackbar('Task created', { variant: 'success' });

      setTaskForm({
        title: '',
        projectId: '',
        priority: 'medium',
        targetDate: '',
        dueDate: '',
        assignedTo: '',
        status: 'to-do',
        collaborators: [],
        tags: '',
        description: '',
        file: null,
      });
      setLinkedProjectIds([]);
      setTaskDialogOpen(false);
      fetchTasks();
    } catch (err) {
      console.error('Create task error:', err);
      const msg = err?.response?.data?.error || 'Failed to create task';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleCloseTaskDialog = () => {
    setTaskDialogOpen(false);
    setTaskForm({
      title: '',
      description: '',
      projectId: selectedProject || '',
      priority: 'medium',
      status: 'to-do',
      dueDate: '',
      targetDate: '',
      assignedTo: '',
      collaborators: [],
    });
    setTaskFormFiles([]);
    setLinkedProjectIds([]);
  };

  const handleInviteAction = async (inviteId, action) => {
    try {
      await api.post(
        `/api/workspace-invitations/${inviteId}/respond`,
        { user_id: userId, action }
      );
      const res = await api.get(
        `/api/my-workspace-invitations?user_id=${userId}`
      );
      setPendingInvites(res.data || []);
      fetchWorkspaces();
      enqueueSnackbar(`Invitation ${action}ed`);
    } catch (err) {
      console.error('Error responding to invite:', err);
      enqueueSnackbar(
        err.response?.data?.error ||
          'Error responding to invitation. Check console for details.'
      );
    }
  };

  const loadTaskComments = async (taskId) => {
    try {
      const res = await api.get(`/api/comments?task_id=${taskId}`);
      setSelectedTaskComments(res.data || []);
    } catch (err) {
      console.error('Error loading task comments:', err);
      setSelectedTaskComments([]);
    }
  };

  const loadTaskSteps = async (taskId) => {
    try {
      const res = await api.get(`/api/task-steps`, {
        params: { task_id: taskId },
      });
      setTaskSteps(res.data || []);
    } catch (err) {
      console.error('Error loading task steps:', err);
      setTaskSteps([]);
    }
  };

  const handleToggleStep = async (stepItemId, currentDone) => {
    if (!selectedTaskForPanel) return;
    try {
      const res = await api.post('/api/task-steps/toggle', {
        task_id: selectedTaskForPanel.id,
        step_item_id: stepItemId,
        user_id: userId,
        is_done: !currentDone,
      });
      const updated = res.data;
      setTaskSteps((prev) =>
        prev.map((s) =>
          s.step_item_id === updated.step_item_id
            ? {
                ...s,
                is_done: updated.is_done,
                done_at: updated.done_at,
                done_by: updated.done_by,
              }
            : s
        )
      );
    } catch (err) {
      console.error('Error toggling step:', err);
      enqueueSnackbar(
        err.response?.data?.error || 'Error updating checklist step'
      );
    }
  };

  const loadAvailableStepGroups = async (task) => {
    if (!selectedWorkspace || !task) {
      setAvailableStepGroups([]);
      return;
    }

    try {
      const res = await api.get('/api/step-groups', {
        params: {
          workspace_id: selectedWorkspace,
          project_id: task.project_id || undefined,
          scope: 'both',
          status: 'approved',
        },
      });
      setAvailableStepGroups(res.data || []);
    } catch (err) {
      console.error(
        'Error loading step groups:',
        err.response?.data || err.message
      );
      setAvailableStepGroups([]);
    }
  };

  const handleAttachStepGroup = async (stepGroupId) => {
    if (!selectedTaskForPanel) return;
    try {
      const res = await api.post(
        `/api/tasks/${selectedTaskForPanel.id}/attach-step-group`,
        {
          user_id: userId,
          step_group_id: stepGroupId,
        }
      );
      const rows = res.data || [];
      setTaskSteps(rows);
    } catch (err) {
      console.error('Error attaching step group:', err);
      enqueueSnackbar(
        err.response?.data?.error ||
          'Error attaching checklist template'
      );
    }
  };

  const loadTaskWorkLogs = async (taskId) => {
    try {
      const res = await api.get('/api/work-logs', {
        params: {
          user_id: userId,
          task_id: taskId,
          view: 'mine',
          workspace_id: selectedWorkspace || undefined,
        },
      });
      setTaskWorkLogs(res.data || []);
    } catch (err) {
      console.error(
        'Error loading task work logs:',
        err.response?.data || err.message
      );
      setTaskWorkLogs([]);
    }
  };

  const loadAvailableWorkLogApprovers = async (task) => {
    if (!userId || !selectedWorkspace || !task) {
      setAvailableWorkLogApprovers([]);
      setTaskWorkLogDraft((prev) => ({
        ...prev,
        approver_user_id: '',
      }));
      return;
    }
    try {
      const res = await api.get('/api/work-logs/eligible-approvers', {
        params: {
          user_id: userId,
          workspace_id: selectedWorkspace,
          project_id: task.project_id || undefined,
          task_id: task.id,
        },
      });
      const rows = res.data || [];
      setAvailableWorkLogApprovers(rows);
      if (rows.length === 1) {
        const only = rows[0];
        const id = only.id ?? only.user_id ?? only.approver_user_id;
        setTaskWorkLogDraft((prev) => ({
          ...prev,
          approver_user_id:
            prev.approver_user_id || (id ? String(id) : ''),
        }));
      }
    } catch (err) {
      console.error(
        'Error loading work-log approvers:',
        err.response?.data || err.message
      );
      setAvailableWorkLogApprovers([]);
      setTaskWorkLogDraft((prev) => ({
        ...prev,
        approver_user_id: prev.approver_user_id || '',
      }));
    }
  };

  const handleAddWorkLog = async () => {
    if (!selectedTaskForPanel) return;
    if (!taskWorkLogDraft.hours && !taskWorkLogDraft.minutes) {
      enqueueSnackbar('Enter hours or minutes');
      return;
    }
    if (
      availableWorkLogApprovers.length > 0 &&
      !taskWorkLogDraft.approver_user_id
    ) {
      enqueueSnackbar('Please select an approver for this work log');
      return;
    }
    try {
      const payload = {
        user_id: userId,
        task_id: selectedTaskForPanel.id,
        work_date:
          taskWorkLogDraft.date ||
          new Date().toISOString().slice(0, 10),
        hours: Number(taskWorkLogDraft.hours || 0),
        minutes: Number(taskWorkLogDraft.minutes || 0),
        notes: taskWorkLogDraft.notes || '',
      };
      if (taskWorkLogDraft.approver_user_id) {
        payload.approver_user_id = Number(
          taskWorkLogDraft.approver_user_id
        );
      }
      const res = await api.post('/api/work-logs', payload);
      const newLog = res.data;
      setTaskWorkLogs((prev) => [newLog, ...prev]);
      setTaskWorkLogDraft((prev) => ({
        ...prev,
        hours: '',
        minutes: '',
        notes: '',
        approver_user_id: prev.approver_user_id,
      }));
    } catch (err) {
      console.error(
        'Error adding work log:',
        err.response?.data || err.message
      );
      enqueueSnackbar(
        err.response?.data?.error ||
          'Error adding work done entry'
      );
    }
  };

  const loadTaskActivityLogs = async (taskId) => {
    if (!taskId) return;
    try {
      const params = {
        task_id: taskId,
        date_from: activityFrom || null,
        date_to: activityTo || null,
        user_id_filter: activityUserFilter || null,
        limit: 50,
        user_id: userId,
      };
      if (selectedWorkspace) {
        params.workspace_id = selectedWorkspace;
      }
      if (selectedProject) {
        params.project_id = selectedProject;
      }
      const res = await api.get('/api/activity-logs', { params });
      let rows = [];
      if (Array.isArray(res.data)) {
        rows = res.data;
      } else if (res.data && Array.isArray(res.data.rows)) {
        rows = res.data.rows;
      }
      setTaskActivityLogs(rows || []);
    } catch (err) {
      console.error(
        'Error loading activity logs:',
        err.response?.data || err.message
      );
      setTaskActivityLogs([]);
    }
  };

  // ✅ UPDATED: can take an explicit taskId OR fall back to selectedTaskForPanel
  const loadTask = async (taskId) => {
    const id = taskId ?? selectedTaskForPanel?.id;
    if (!id) return;
    try {
      const res = await api.get(`/api/tasks/${id}`);
      setSelectedTaskForPanel(res.data);
    } catch (err) {
      console.error('Error loading task:', err);
    }
  };

  const handleApproveWorkLog = async (logId) => {
    try {
      const res = await api.post(`/api/work-logs/${logId}/approve`, {
        user_id: userId,
      });
      const updated = res.data;
      setWorkDoneRows((prev) =>
        prev.map((r) => (r.id === updated.id ? updated : r))
      );
      setTaskWorkLogs((prev) =>
        prev.map((r) => (r.id === updated.id ? updated : r))
      );
    } catch (err) {
      console.error('Error approving work log:', err);
      enqueueSnackbar(
        err.response?.data?.error ||
          'Error approving work done entry'
      );
    }
  };

  const openTaskPanel = (task) => {
    const extras = Array.isArray(task.project_ids)
      ? task.project_ids.filter(
          (id) => String(id) !== String(task.project_id)
        )
      : [];
    setLinkedProjectIds(extras);
    setSelectedTaskForPanel(task);
    setTaskPanelOpen(true);
    setTaskDetailTab('comments');
    loadTaskComments(task.id);
    loadTaskSteps(task.id);
    loadTaskWorkLogs(task.id);
    loadTaskActivityLogs(task.id);
    loadAvailableWorkLogApprovers(task);
    if (selectedWorkspace) {
      loadAvailableStepGroups(task);
    } else {
      setAvailableStepGroups([]);
    }
  };

  const handleSaveTaskDetails = async (changes = {}) => {
    if (!selectedTaskForPanel) return false;
    const payload = {
      user_id: userId,
      ...changes,
    };
    if (Array.isArray(payload.tags)) {
      payload.tags = payload.tags
        .map((t) => String(t).trim())
        .filter(Boolean)
        .join(',');
    }

    try {
      const res = await api.put(
        `/api/tasks/${selectedTaskForPanel.id}`,
        payload
      );
      const updated = res.data;

      setTasks((prev) =>
        (prev || []).map((t) => (t.id === updated.id ? updated : t))
      );
      setAssignedTasks((prev) =>
        (prev || []).map((t) => (t.id === updated.id ? updated : t))
      );
      setSelectedTaskForPanel(updated);

      return true;
    } catch (err) {
      console.error('Error in handleSaveTaskDetails:', err);
      const msg =
        err?.response?.data?.error || 'Failed to save task details';
      enqueueSnackbar(msg, { variant: 'error' });
      return false;
    }
  };

  const handleArchiveTask = async () => {
    if (!selectedTaskForPanel) return;
    if (selectedTaskForPanel.status !== 'completed') {
      enqueueSnackbar(
        'Task must be marked Completed before it can be archived.'
      );
      return;
    }
    try {
      setTaskDetailSaving(true);
      const res = await api.put(
        `/api/tasks/${selectedTaskForPanel.id}`,
        {
          user_id: userId,
          archived: true,
        }
      );
      const updated = res.data;
      setTasks((prev) =>
        prev.map((task) => (task.id === updated.id ? updated : task))
      );
      setAssignedTasks((prev) =>
        prev.map((task) => (task.id === updated.id ? updated : task))
      );
      setSelectedTaskForPanel(updated);
    } catch (err) {
      console.error('Error archiving task:', err);
      enqueueSnackbar(
        err.response?.data?.error ||
          'Error archiving task. Check console for details.'
      );
    } finally {
      setTaskDetailSaving(false);
    }
  };

  const handleAddTaskComment = async () => {
    if (!selectedTaskForPanel || !taskCommentDraft.trim()) return;
    try {
      const res = await api.post('/api/comments', {
        task_id: selectedTaskForPanel.id,
        user_id: userId,
        content: taskCommentDraft.trim(),
      });
      setSelectedTaskComments((prev) => [res.data, ...prev]);
      setTaskCommentDraft('');
    } catch (err) {
      console.error('Error adding comment:', err);
      enqueueSnackbar('Error adding comment');
    }
  };

  const handleCollaboratorsChange = async (newUserIdsRaw) => {
    if (!selectedTaskForPanel) return;

    const taskId = selectedTaskForPanel.id;

    const normalizeToId = (item) => {
      if (!item) return null;
      if (typeof item === 'number' || typeof item === 'string') {
        const n = Number(item);
        return Number.isNaN(n) ? null : n;
      }
      if (typeof item === 'object') {
        if (item.id != null) {
          const n = Number(item.id);
          return Number.isNaN(n) ? null : n;
        }
        if (item.user_id != null) {
          const n = Number(item.user_id);
          return Number.isNaN(n) ? null : n;
        }
      }
      return null;
    };

    const newIds = (newUserIdsRaw || [])
      .map(normalizeToId)
      .filter((id) => id != null);

    try {
      await api.put(`/api/tasks/${taskId}`, {
        user_id: userId,
        collaborator_ids: newIds,
      });

      const updatedCollaborators = assignableUsers
        .filter((u) => newIds.includes(Number(u.id)))
        .map((u) => ({
          user_id: u.id,
          username: u.username || u.email,
        }));

      setSelectedTaskForPanel((prev) =>
        prev ? { ...prev, collaborators: updatedCollaborators } : prev
      );

      setTasks((prev) =>
        (prev || []).map((t) =>
          t.id === taskId ? { ...t, collaborators: updatedCollaborators } : t
        )
      );

      setAssignedTasks((prev) =>
        (prev || []).map((t) =>
          t.id === taskId ? { ...t, collaborators: updatedCollaborators } : t
        )
      );

      fetchTasks();
    } catch (err) {
      console.error(
        'Error updating collaborators:',
        err.response?.data || err.message
      );
      enqueueSnackbar(
        err.response?.data?.error || 'Failed to update collaborators',
        { variant: 'error' }
      );
    }
  };

  const getUserName = (id) => {
    if (!id) return '';
    const all = [...workspaceUsers, ...projectUsers];
    const found = all.find((u) => Number(u.id) === Number(id));
    return found ? found.username : `User ${id}`;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      <DashboardView
        currentView={currentView}
        sidebarAutoCollapse={sidebarAutoCollapse}
        setSidebarAutoCollapse={setSidebarAutoCollapse}
        sidebarHoverExpand={sidebarHoverExpand}
        setSidebarHoverExpand={setSidebarHoverExpand}
        sidebarHovering={sidebarHovering}
        setSidebarHovering={setSidebarHovering}
        greeting={greeting}
        selectedWorkspaceObj={selectedWorkspaceObj}
        selectedProjectObj={selectedProjectObj}
        viewMode={viewMode}
        setViewMode={setViewMode}
        showArchivedTasks={showArchivedTasks}
        setShowArchivedTasks={setShowArchivedTasks}
        taskStatusFilter={taskStatusFilter}
        setTaskStatusFilter={setTaskStatusFilter}
        taskPriorityFilter={taskPriorityFilter}
        setTaskPriorityFilter={setTaskPriorityFilter}
        taskAssigneeFilter={taskAssigneeFilter}
        setTaskAssigneeFilter={setTaskAssigneeFilter}
        taskTagFilter={taskTagFilter}
        setTaskTagFilter={setTaskTagFilter}
        taskDuePreset={taskDuePreset}
        setTaskDuePreset={setTaskDuePreset}
        taskTargetPreset={taskTargetPreset}
        setTaskTargetPreset={setTaskTargetPreset}
        taskCreatorFilter={taskCreatorFilter}
        setTaskCreatorFilter={setTaskCreatorFilter}
        taskDueCustomFrom={taskDueCustomFrom}
        taskDueCustomTo={taskDueCustomTo}
        setTaskDueCustomFrom={setTaskDueCustomFrom}
        setTaskDueCustomTo={setTaskDueCustomTo}
        taskTargetCustomFrom={taskTargetCustomFrom}
        taskTargetCustomTo={taskTargetCustomTo}
        setTaskTargetCustomFrom={setTaskTargetCustomFrom}
        setTaskTargetCustomTo={setTaskTargetCustomTo}
        taskQuickSearch={taskQuickSearch}
        setTaskQuickSearch={setTaskQuickSearch}
        homeTableColumns={homeTableColumns}
        setHomeTableColumns={setHomeTableColumns}
        loadingTasks={loadingTasks}
        topBarUserName={topBarUserName}
        selectedWorkspace={selectedWorkspace}
        selectedProject={selectedProject}
        workspaces={workspaces}
        projects={projects}
        tasks={tasks}
        assignedTasks={assignedTasks}
        workspaceUsers={workspaceUsers}
        projectUsers={projectUsers}
        goals={goals}
        handleOpenProfileMenu={handleOpenProfileMenu}
        profileMenuAnchor={profileMenuAnchor}
        handleCloseProfileMenu={handleCloseProfileMenu}
        setProfileOpen={setProfileOpen}
        setPermissionGridOpen={setPermissionGridOpen}
        setArchiveOpen={setArchiveOpen}
        setProjectDialogOpen={setProjectDialogOpen}
        setCustomRoleDialogOpen={setCustomRoleDialogOpen}
        setRoleManagerOpen={setRoleManagerOpen}
        setProjectRoleManagerOpen={setProjectRoleManagerOpen}
        setInviteDialogOpen={setInviteDialogOpen}
        handleWorkspaceChange={handleWorkspaceChange}
        handleModify={handleModify}
        openTaskPanel={openTaskPanel}
        getUserName={getUserName}
        handleApproveWorkLog={handleApproveWorkLog}
        workDoneViewMode={workDoneViewMode}
        workDoneRows={workDoneRows}
        workDoneSummary={workDoneSummary}
        workDoneRangeKey={workDoneRangeKey}
        setWorkDoneRangeKey={setWorkDoneRangeKey}
        workDoneCustomFrom={workDoneCustomFrom}
        setWorkDoneCustomFrom={setWorkDoneCustomFrom}
        workDoneCustomTo={workDoneCustomTo}
        setWorkDoneCustomTo={setWorkDoneCustomTo}
        workDoneStatusFilter={workDoneStatusFilter}
        setWorkDoneStatusFilter={setWorkDoneStatusFilter}
        workDoneProjectFilterIds={workDoneProjectFilterIds}
        setWorkDoneProjectFilterIds={setWorkDoneProjectFilterIds}
        selectedTasks={selectedTasks}
        setSelectedTasks={setSelectedTasks}
        bulkDialogOpen={bulkDialogOpen}
        setBulkDialogOpen={setBulkDialogOpen}
        bulkForm={bulkForm}
        setBulkForm={setBulkForm}
        projectRecurring={projectRecurring}
        recurringLoading={recurringLoading}
        adminRecurring={adminRecurring}
        adminRecurringLoading={adminRecurringLoading}
        adminCronRunning={adminCronRunning}
        handleRecurringAction={handleRecurringAction}
        handleRunRecurringNow={handleRunRecurringNow}
        loadingWorkspaces={loadingWorkspaces}
        loadingProjects={loadingProjects}
        loadingWorkspaceUsers={loadingWorkspaceUsers}
        loadingProjectUsers={loadingProjectUsers}
        enqueueSnackbar={enqueueSnackbar}
        projectDialogOpen={projectDialogOpen}
        projectName={projectName}
        setProjectName={setProjectName}
        handleCreateProject={handleCreateProject}
        taskDialogOpen={taskDialogOpen}
        setTaskDialogOpen={setTaskDialogOpen}
        taskForm={taskForm}
        setTaskForm={setTaskForm}
        linkedProjectIds={linkedProjectIds}
        setLinkedProjectIds={setLinkedProjectIds}
        handleCreateTask={handleCreateTask}
        taskFormFiles={taskFormFiles}
        setTaskFormFiles={setTaskFormFiles}
        handleCloseTaskDialog={handleCloseTaskDialog}
        taskPanelOpen={taskPanelOpen}
        setTaskPanelOpen={setTaskPanelOpen}
        selectedTaskForPanel={selectedTaskForPanel}
        setSelectedTaskForPanel={setSelectedTaskForPanel}
        selectedTaskComments={selectedTaskComments}
        setSelectedTaskComments={setSelectedTaskComments}
        taskCommentDraft={taskCommentDraft}
        setTaskCommentDraft={setTaskCommentDraft}
        taskSteps={taskSteps}
        setTaskSteps={setTaskSteps}
        availableStepGroups={availableStepGroups}
        setAvailableStepGroups={setAvailableStepGroups}
        selectedStepGroupForAttach={selectedStepGroupForAttach}
        setSelectedStepGroupForAttach={setSelectedStepGroupForAttach}
        taskWorkLogs={taskWorkLogs}
        setTaskWorkLogs={setTaskWorkLogs}
        taskActivityLogs={taskActivityLogs}
        setTaskActivityLogs={setTaskActivityLogs}
        taskDetailTab={taskDetailTab}
        setTaskDetailTab={setTaskDetailTab}
        activityFrom={activityFrom}
        setActivityFrom={setActivityFrom}
        activityTo={activityTo}
        setActivityTo={setActivityTo}
        activityUserFilter={activityUserFilter}
        setActivityUserFilter={setActivityUserFilter}
        taskWorkLogDraft={taskWorkLogDraft}
        setTaskWorkLogDraft={setTaskWorkLogDraft}
        availableWorkLogApprovers={availableWorkLogApprovers}
        setAvailableWorkLogApprovers={setAvailableWorkLogApprovers}
        userId={userId}
        handleSaveTaskDetails={handleSaveTaskDetails}
        handleArchiveTask={handleArchiveTask}
        handleAddTaskComment={handleAddTaskComment}
        handleCollaboratorsChange={handleCollaboratorsChange}
        loadTaskComments={loadTaskComments}
        handleToggleStep={handleToggleStep}
        handleAttachStepGroup={handleAttachStepGroup}
        handleAddWorkLog={handleAddWorkLog}
        loadTask={loadTask}
        loadTaskActivityLogs={loadTaskActivityLogs}
        handleSaveProjectNotes={handleSaveProjectNotes}
        projectSaving={projectSaving}
        fetchTasks={fetchTasks}
        taskDetailSaving={taskDetailSaving}
        canLogWorkOnSelectedTask={canLogWorkOnSelectedTask}
        loadTaskWorkLogs={loadTaskWorkLogs}
        setTaskDetailSaving={setTaskDetailSaving}
        roleManagerOpen={roleManagerOpen}
        projectRoleManagerOpen={projectRoleManagerOpen}
        inviteDialogOpen={inviteDialogOpen}
        permissionGridOpen={permissionGridOpen}
        customRoles={customRoles}
        archiveOpen={archiveOpen}
        editingProject={editingProject}
        customRoleDialogOpen={customRoleDialogOpen}
        profileOpen={profileOpen}
        workDoneSelectedDate={workDoneSelectedDate}
        setSelectedProject={setSelectedProject}
        setCurrentView={setCurrentView}
        onToggleMode={onToggleMode}
        setLogoutDialogOpen={setLogoutDialogOpen}
        logoutDialogOpen={logoutDialogOpen}
        onLogout={onLogout}
        pendingInvites={pendingInvites}
        handleInviteAction={handleInviteAction}
        setShowApprovalMatrix={setShowApprovalMatrix}
        showApprovalMatrix={showApprovalMatrix}
        setWorkDoneViewMode={setWorkDoneViewMode}
        setWorkDoneSelectedDate={setWorkDoneSelectedDate}
        loadAdminRecurring={loadAdminRecurring}
        workspaceTab={workspaceTab}
        setWorkspaceTab={setWorkspaceTab}
        setWorkspaceLogs={setWorkspaceLogs}
        workspaceLogs={workspaceLogs}
        fetchWorkspaces={fetchWorkspaces}
        projectNotesDraft={projectNotesDraft}
        setProjectNotesDraft={setProjectNotesDraft}
        setEditingProject={setEditingProject}
        setTasks={setTasks}
        fetchProjects={fetchProjects}
        api={api}
        API_BASE={API_BASE}
        HOME_TABLE_AVAILABLE_COLUMNS={HOME_TABLE_AVAILABLE_COLUMNS}
        setSelectedWorkspace={setSelectedWorkspace}
      />
    </Box>
  );
}

export default Dashboard;
